import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Button } from '../../components';
import Switch from './Switch';
import InlineConfirm from './InlineConfirm';
import BrandAssetsPanel from './BrandAssetsPanel';
import {
    fetchSettings,
    fetchHealth,
    saveSettings,
    fetchExtensionKey,
    rotateExtensionKey,
    fetchStorritoAccounts,
} from './AdminSocialPostsPage.duck';
import css from './AdminSocialPostsPage.css';

const PLATFORMS = ['facebook', 'instagram', 'linkedin'];
const PLATFORM_LABELS = { facebook: 'Facebook', instagram: 'Instagram', linkedin: 'LinkedIn' };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DEFAULT_APPROVAL_EMAIL = {
    subject: 'Neuer HorseDeal Social-Entwurf wartet auf Freigabe: {{entityTitle}}',
    bodyHtml: [
        '<div style="margin:0;padding:0;background:#f6f4f2;font-family:Arial,Helvetica,sans-serif;color:#1a2b49">',
        '  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="border-collapse:collapse;background:#f6f4f2;padding:24px 0">',
        '    <tr><td align="center" style="padding:24px 12px">',
        '      <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="max-width:640px;border-collapse:collapse;background:#ffffff;border-radius:12px;overflow:hidden;border:1px solid #e6e1dc">',
        '        <tr><td><img src="{{imageUrl}}" alt="" width="640" style="display:block;width:100%;max-height:280px;object-fit:cover;background:#eee7e1" /></td></tr>',
        '        <tr><td style="padding:28px 30px 30px">',
        '          <p style="margin:0 0 8px;font-size:13px;line-height:18px;color:#8f2593;font-weight:700;text-transform:uppercase;letter-spacing:.04em">{{triggerLabel}}</p>',
        '          <h1 style="margin:0 0 12px;font-size:24px;line-height:31px;color:#1a2b49">Ein neuer Social-Entwurf ist bereit</h1>',
        '          <p style="margin:0 0 18px;font-size:16px;line-height:24px;color:#34405c">Für <strong>{{entityTitle}}</strong> wurde ein neuer Beitrag für {{platforms}} erstellt.</p>',
        '          <table role="presentation" cellspacing="0" cellpadding="0" style="margin:0 0 22px;border-collapse:collapse"><tr><td style="border-radius:6px;background:#8f2593"><a href="{{reviewUrl}}" style="display:inline-block;padding:13px 18px;font-size:15px;line-height:20px;color:#ffffff;text-decoration:none;font-weight:700">Entwurf direkt prüfen</a></td></tr></table>',
        '          <p style="margin:0 0 10px;font-size:14px;line-height:21px;color:#596173">Freigabe-Seite: <a href="{{reviewUrl}}" style="color:#8f2593;text-decoration:underline">{{reviewUrl}}</a></p>',
        '          <p style="margin:0;font-size:14px;line-height:21px;color:#596173">Profil/Inserat: <a href="{{primaryUrl}}" style="color:#8f2593;text-decoration:underline">{{primaryUrl}}</a></p>',
        '        </td></tr>',
        '      </table>',
        '    </td></tr>',
        '  </table>',
        '</div>',
    ].join('\n'),
};
const SAMPLE_EMAIL_VARS = {
    postId: '1042',
    reviewUrl: 'https://horsedeal.com/admin-social/review?post=1042',
    entityTitle: 'Freundlicher Wallach Rocky',
    listingTitle: 'Freundlicher Wallach Rocky',
    triggerLabel: 'Neues Inserat',
    platforms: 'Facebook, Facebook-Community, Instagram',
    imageUrl: 'https://images.unsplash.com/photo-1553284965-83fd3e82fa5a?w=1200&q=80',
    primaryUrl: 'https://horsedeal.com/l/rocky/abc123',
    listingUrl: 'https://horsedeal.com/l/rocky/abc123',
    profileUrl: '',
    createdAt: '1. Juli 2026, 14:30',
};
const renderTemplate = (template, values) =>
    String(template || '').replace(/\{\{\s*(\w+)\s*\}\}/g, (_m, key) =>
        values && values[key] != null ? String(values[key]) : ''
    );
const normalizeApprovalEmail = cfg => ({
    subject:
        cfg && cfg.approvalEmail && typeof cfg.approvalEmail.subject === 'string'
            ? cfg.approvalEmail.subject
            : DEFAULT_APPROVAL_EMAIL.subject,
    bodyHtml:
        cfg && cfg.approvalEmail && typeof cfg.approvalEmail.bodyHtml === 'string'
            ? cfg.approvalEmail.bodyHtml
            : DEFAULT_APPROVAL_EMAIL.bodyHtml,
});

// Sentinel select option that reveals the free-text username field — so an
// operator can always type an account Storrito hasn't listed yet (dry-run lists
// none), never trapped by the dropdown.
const CUSTOM_USERNAME = '__custom__';

// The subset of fields the form actually edits. Serialised deterministically so
// "dirty" is a reliable diff against the last-saved settings (the store also
// carries computed fields like designPresets that the form never touches).
const editable = cfg => ({
    enabled: !!cfg.enabled,
    autoApprove: !!cfg.autoApprove,
    socialManagerEmail: cfg.socialManagerEmail || '',
    approvalEmail: normalizeApprovalEmail(cfg),
    postizMode: cfg.postizMode === 'draft' ? 'draft' : 'now',
    postizIntegrationIds: PLATFORMS.reduce((acc, p) => {
        acc[p] = (cfg.postizIntegrationIds || {})[p] || '';
        return acc;
    }, {}),
    postizMaxPerTick: cfg.postizMaxPerTick,
    // Instagram Stories via Storrito (native, tappable link sticker).
    storritoEnabled: !!cfg.storritoEnabled,
    storritoInstagramUsername: cfg.storritoInstagramUsername || '',
    // The stakeholder-selected fourth Storrito style is a durable output contract,
    // not an operator preference. This also heals legacy values on the next save.
    storritoLinkDesign: 'rainbow',
});
const serialize = cfg => JSON.stringify(editable(cfg));

// Blocking validation — anything here disables Save. Soft warnings (e.g. a
// missing integration id while automation is on) never block, they only guide.
const validate = cfg => {
    const errors = {};
    const email = (cfg.socialManagerEmail || '').trim();
    if (email && !EMAIL_RE.test(email)) {
        errors.socialManagerEmail = 'Bitte eine gültige E-Mail-Adresse eingeben.';
    }
    const n = Number(cfg.postizMaxPerTick);
    if (!Number.isInteger(n) || n < 1) {
        errors.postizMaxPerTick = 'Mindestens 1 Beitrag pro Durchlauf.';
    }
    return errors;
};

const Section = ({ title, description, children }) => (
    <section className={css.section}>
        <div className={css.sectionHead}>
            <h3 className={css.sectionTitle}>{title}</h3>
            {description && <p className={css.sectionDesc}>{description}</p>}
        </div>
        <div className={css.sectionBody}>{children}</div>
    </section>
);

const OptionRow = ({ title, helper, children }) => (
    <div className={css.optionRow}>
        <div className={css.optionText}>
            <span className={css.optionTitle}>{title}</span>
            {helper && <p className={css.optionHelper}>{helper}</p>}
        </div>
        <div className={css.optionControl}>{children}</div>
    </div>
);

const EmailPreview = ({ recipient, approvalEmail }) => {
    const subject = renderTemplate(approvalEmail.subject, SAMPLE_EMAIL_VARS);
    const bodyHtml = renderTemplate(approvalEmail.bodyHtml, SAMPLE_EMAIL_VARS);
    return (
        <div className={css.emailPreview}>
            <div className={css.emailPreviewMeta}>
                <span>An: {recipient || 'social@horsedeal.com'}</span>
                <strong>{subject || '(kein Betreff)'}</strong>
            </div>
            <div className={css.emailPreviewFrame} dangerouslySetInnerHTML={{ __html: bodyHtml }} />
        </div>
    );
};

const SettingsSkeleton = () => (
    <div className={css.settings} aria-busy="true" aria-label="Einstellungen werden geladen">
        {[0, 1, 2].map(i => (
            <div key={i} className={css.skeletonSection}>
                <div className={`${css.skel} ${css.skelTitle}`} />
                <div className={`${css.skel} ${css.skelLine}`} />
                <div className={`${css.skel} ${css.skelRow}`} />
                <div className={`${css.skel} ${css.skelRow}`} />
            </div>
        ))}
    </div>
);

const SocialSettingsForm = props => {
    const {
        settings,
        fetchSettingsInProgress,
        fetchSettingsError,
        saveSettingsInProgress,
        saveSettingsError,
        settingsSaved,
        health,
        healthError,
        runtimeMode,
        onFetchSettings,
        onFetchHealth,
        onSave,
        onFetchExtensionKey,
        onRotateExtensionKey,
        onFetchStorritoAccounts,
    } = props;

    const [config, setConfig] = useState(null);
    // The connected Storrito IG accounts feed the username picker. Display-only
    // helper data (never part of the saved config), fetched independently like the
    // extension key, so it lives in local state.
    const [storritoAccounts, setStorritoAccounts] = useState([]);
    // The operator chose "Anderer Benutzername…" — reveal the free-text field even
    // when connected accounts exist (lets them type one Storrito hasn't listed yet).
    const [usernameCustom, setUsernameCustom] = useState(false);
    // The browser-extension API key is display-only (its own setting row, never
    // touched by Save), so it lives in local state, fetched independently.
    const [extKey, setExtKey] = useState('');
    const [extKeyShown, setExtKeyShown] = useState(false);
    const [extKeyBusy, setExtKeyBusy] = useState(false);
    const [extKeyCopied, setExtKeyCopied] = useState(false);
    const [extKeyError, setExtKeyError] = useState(null);
    const [emailSettingsOpen, setEmailSettingsOpen] = useState(false);
    // Which fields the operator has actually edited this session. Validation only
    // surfaces (and only blocks Save) for touched fields — a pre-existing saved
    // value the user hasn't touched is treated as accepted, so the panel never
    // greets them with a red error on load.
    const [touched, setTouched] = useState({});

    useEffect(() => {
        onFetchSettings();
        onFetchHealth();
        onFetchExtensionKey()
            .then(key => setExtKey(key || ''))
            .catch(() => {});
        // Best-effort: a fault leaves the list empty and the picker falls back to
        // free-text (the Storrito connection error is shown by the health row).
        onFetchStorritoAccounts()
            .then(accounts => setStorritoAccounts(Array.isArray(accounts) ? accounts : []))
            .catch(() => setStorritoAccounts([]));
    }, [onFetchSettings, onFetchHealth, onFetchExtensionKey, onFetchStorritoAccounts]);

    // Sync the editable copy when the store's settings load or change (incl. after
    // a successful save, which re-baselines "dirty" and clears touched state).
    useEffect(() => {
        if (settings) {
            setConfig(settings);
            setTouched({});
        }
    }, [settings]);

    if (fetchSettingsInProgress && !config) return <SettingsSkeleton />;

    if (!config) {
        return (
            <div className={css.settings}>
                <div className={css.loadError}>
                    <p className={css.loadErrorTitle}>Einstellungen nicht verfügbar</p>
                    {fetchSettingsError && (
                        <p className={css.loadErrorBody}>{fetchSettingsError}</p>
                    )}
                    <Button
                        className={css.secondaryButton}
                        onClick={() => {
                            onFetchSettings();
                            onFetchHealth();
                        }}
                    >
                        Erneut versuchen
                    </Button>
                </div>
            </div>
        );
    }

    const touch = key => setTouched(prev => (prev[key] ? prev : { ...prev, [key]: true }));
    const set = (key, value) => setConfig(prev => ({ ...prev, [key]: value }));
    const setApprovalEmail = (key, value) =>
        setConfig(prev => ({
            ...prev,
            approvalEmail: { ...normalizeApprovalEmail(prev), [key]: value },
        }));
    const setNested = (key, subKey, value) =>
        setConfig(prev => ({ ...prev, [key]: { ...(prev[key] || {}), [subKey]: value } }));

    const errors = validate(config);
    const showError = key => (touched[key] ? errors[key] : null);
    const blocking = Object.keys(errors).some(k => touched[k]);
    const dirty = settings ? serialize(config) !== serialize(settings) : false;
    const canSave = dirty && !blocking && !saveSettingsInProgress;

    const handleSave = () => {
        if (!canSave) return;
        onSave(config);
    };
    const handleDiscard = () => {
        setConfig(settings);
        setTouched({});
    };

    const handleCopyKey = () => {
        if (!extKey) return;
        try {
            navigator.clipboard.writeText(extKey);
            setExtKeyCopied(true);
            setTimeout(() => setExtKeyCopied(false), 1800);
        } catch (e) {
            // Clipboard can be blocked (insecure context) — reveal so it can be
            // copied by hand instead of silently failing.
            setExtKeyShown(true);
        }
    };
    // The confirm itself is the shared inline two-step (InlineConfirm below); a
    // failure reports IN the form, where the operator is still looking, instead of
    // in a window.alert stacked on a dialog they already dismissed.
    const handleRotateKey = async () => {
        if (extKeyBusy) return;
        setExtKeyBusy(true);
        setExtKeyError(null);
        try {
            const key = await onRotateExtensionKey();
            setExtKey(key || '');
            setExtKeyShown(true);
        } catch (e) {
            setExtKeyError(`Schlüssel konnte nicht generiert werden: ${e.message}`);
        } finally {
            setExtKeyBusy(false);
        }
    };
    const maskedKey = extKey
        ? extKeyShown
            ? extKey
            : `${'•'.repeat(Math.max(0, extKey.length - 4))}${extKey.slice(-4)}`
        : '';

    const isDraft = config.postizMode === 'draft';
    const liveAutopublish = !isDraft && config.enabled && config.autoApprove;

    const healthFor = integrationId => {
        if (!Array.isArray(health)) return null;
        return health.find(h => String(h.integrationId) === String(integrationId));
    };

    // The connected/not-connected pill for ONE health row — shared by the Postiz
    // per-integration lookup and the Storrito provider rows so both read identically.
    const healthPill = h =>
        h.connected ? (
            <span className={`${css.statusPill} ${css.statusPublished}`}>
                ✓ Verbunden{h.name ? ` · ${h.name}` : ''}
            </span>
        ) : (
            <span className={`${css.statusPill} ${css.statusPending}`}>
                ⚠ {h.lastError || 'Nicht verbunden'}
            </span>
        );

    const renderHealthPill = id => {
        if (!id) return null;
        const h = healthFor(id);
        if (!h) {
            return (
                <span className={`${css.statusPill} ${css.statusNeutral}`}>Status unbekannt</span>
            );
        }
        return healthPill(h);
    };

    // Storrito connection health: health() tags each row with a provider, so we
    // show just the Storrito rows here (Postiz rows keep rendering above, unchanged).
    const storritoHealthRows = Array.isArray(health)
        ? health.filter(h => h.provider === 'storrito')
        : [];
    const storritoConnected = storritoHealthRows.some(h => h.connected);
    // Preflight: enabled in the form but no connected Storrito account (env missing
    // or token wrong). The amber note guides the operator to the env vars.
    const storritoPreflight = config.storritoEnabled && !storritoConnected;

    // Username picker: a validated select over the connected accounts, with a
    // free-text fallback so a not-yet-listed account is always reachable.
    const accountUsernames = storritoAccounts.map(a => a && a.instagramUsername).filter(Boolean);
    const currentUsername = config.storritoInstagramUsername || '';
    const knownAccount = !!currentUsername && accountUsernames.includes(currentUsername);
    const showUsernameField =
        accountUsernames.length === 0 || usernameCustom || (!!currentUsername && !knownAccount);
    // Highlight the matching account when known; otherwise the "Anderer…" row
    // (a custom/not-yet-listed value), or the empty prompt when nothing is set.
    const usernameSelectValue = knownAccount
        ? currentUsername
        : currentUsername || usernameCustom
        ? CUSTOM_USERNAME
        : '';
    const handleUsernameSelect = e => {
        const v = e.target.value;
        if (v === CUSTOM_USERNAME) {
            setUsernameCustom(true); // reveal free-text, keep the current value
        } else {
            setUsernameCustom(false);
            set('storritoInstagramUsername', v);
        }
    };

    return (
        <div className={css.settings}>
            <header className={css.settingsIntro}>
                <h2 className={css.settingsTitle}>Einstellungen</h2>
                <p className={css.settingsLead}>
                    Steuere, wie HorseDeal Beiträge automatisch erstellt, freigibt und
                    veröffentlicht.
                </p>
            </header>

            {runtimeMode && (
                <div
                    className={`${css.runtimeBanner} ${
                        runtimeMode.socialPublisher === 'local' ? css.runtimeBannerWarn : ''
                    }`}
                >
                    <span>
                        Postiz:{' '}
                        {runtimeMode.socialPublisher === 'local'
                            ? 'Lokal simuliert (kein echter Versand!)'
                            : runtimeMode.postizForceDraft
                            ? 'Live (Entwurf-Modus erzwungen)'
                            : 'Live'}
                    </span>
                    <span>
                        · Storrito:{' '}
                        {runtimeMode.storritoDryRun ? 'Testmodus' : 'Live (echter Versand)'}
                    </span>
                    <span>
                        · Zeitplan-Automatik: {runtimeMode.schedulesEnabled ? 'aktiv' : 'inaktiv'}
                    </span>
                </div>
            )}

            {saveSettingsError && (
                <div className={css.saveError} role="alert">
                    <span>Speichern fehlgeschlagen: {saveSettingsError}</span>
                    <button type="button" className={css.saveErrorRetry} onClick={handleSave}>
                        Erneut speichern
                    </button>
                </div>
            )}

            <Section
                title="Automatisierung"
                description="Wann erstellt HorseDeal Beiträge und wer gibt sie frei."
            >
                <OptionRow
                    title="Automatisierung aktiv"
                    helper="Erstellt bei einem Auslöser (z. B. neues Inserat) automatisch Beiträge."
                >
                    <Switch
                        checked={!!config.enabled}
                        onChange={v => set('enabled', v)}
                        onLabel="Aktiv"
                        offLabel="Inaktiv"
                        tone="success"
                        ariaLabel="Automatisierung aktiv"
                    />
                </OptionRow>

                <OptionRow
                    title="Automatisch freigeben"
                    helper="Überspringt die manuelle Prüfung — Beiträge werden ohne Freigabe veröffentlicht."
                >
                    <Switch
                        checked={!!config.autoApprove}
                        onChange={v => set('autoApprove', v)}
                        onLabel="An"
                        offLabel="Aus"
                        tone="success"
                        ariaLabel="Automatisch freigeben"
                    />
                </OptionRow>

                <div className={css.field}>
                    <label className={css.label} htmlFor="socialManagerEmail">
                        Empfänger der Freigabe-E-Mail
                    </label>
                    <input
                        id="socialManagerEmail"
                        className={`${css.input} ${
                            showError('socialManagerEmail') ? css.inputInvalid : ''
                        }`}
                        type="email"
                        value={config.socialManagerEmail || ''}
                        onChange={e => {
                            set('socialManagerEmail', e.target.value);
                            touch('socialManagerEmail');
                        }}
                        placeholder="social@horsedeal.com"
                        aria-invalid={showError('socialManagerEmail') ? 'true' : undefined}
                    />
                    {showError('socialManagerEmail') ? (
                        <p className={css.fieldError}>{errors.socialManagerEmail}</p>
                    ) : (
                        <p className={css.hint}>
                            Diese Adresse erhält eine Nachricht, sobald ein neuer Beitrag zur
                            Prüfung bereitsteht.
                        </p>
                    )}
                    <button
                        type="button"
                        className={css.emailAdvancedToggle}
                        onClick={() => setEmailSettingsOpen(open => !open)}
                        aria-expanded={emailSettingsOpen}
                        aria-controls="approvalEmailAdvancedSettings"
                    >
                        {emailSettingsOpen
                            ? 'Erweiterte E-Mail-Einstellungen ausblenden'
                            : 'Erweiterte E-Mail-Einstellungen öffnen'}
                    </button>
                </div>

                <p className={css.hint}>
                    Plattformen, Prompt-Vorlagen, Design und das KI-Modell werden im{' '}
                    <strong>Workflow</strong>-Editor festgelegt.
                </p>
            </Section>

            {emailSettingsOpen && (
                <Section
                    title="Freigabe-E-Mail"
                    description="Eine globale Vorlage für alle Workflows. Die Schaltfläche im E-Mail führt direkt zum neuen Review-Eintrag."
                >
                    <div id="approvalEmailAdvancedSettings" className={css.advancedEmailSettings}>
                        <div className={css.field}>
                            <label className={css.label} htmlFor="approvalEmailSubject">
                                Betreff
                            </label>
                            <input
                                id="approvalEmailSubject"
                                className={css.input}
                                value={normalizeApprovalEmail(config).subject}
                                onChange={e => setApprovalEmail('subject', e.target.value)}
                            />
                        </div>
                        <div className={css.field}>
                            <label className={css.label} htmlFor="approvalEmailBody">
                                HTML
                            </label>
                            <textarea
                                id="approvalEmailBody"
                                className={`${css.textarea} ${css.emailHtmlInput}`}
                                value={normalizeApprovalEmail(config).bodyHtml}
                                onChange={e => setApprovalEmail('bodyHtml', e.target.value)}
                                spellCheck={false}
                            />
                            <p className={css.hint}>
                                Platzhalter: {'{{entityTitle}}'}, {'{{triggerLabel}}'},{' '}
                                {'{{platforms}}'}, {'{{imageUrl}}'}, {'{{reviewUrl}}'},{' '}
                                {'{{primaryUrl}}'}, {'{{createdAt}}'}.
                            </p>
                        </div>
                        <EmailPreview
                            recipient={config.socialManagerEmail}
                            approvalEmail={normalizeApprovalEmail(config)}
                        />
                        <button
                            type="button"
                            className={css.inlineTextButton}
                            onClick={() => set('approvalEmail', DEFAULT_APPROVAL_EMAIL)}
                        >
                            Standardvorlage wiederherstellen
                        </button>
                    </div>
                </Section>
            )}

            <Section
                title="Veröffentlichung"
                description="Was passiert, sobald ein Beitrag freigegeben ist."
            >
                <OptionRow
                    title="Als Entwurf an Postiz senden"
                    helper={
                        isDraft
                            ? 'Beiträge landen als Entwurf im Postiz-Kalender — dort verschieben und manuell live schalten.'
                            : 'Freigegebene Beiträge werden sofort über Postiz live geschaltet.'
                    }
                >
                    <Switch
                        checked={isDraft}
                        onChange={v => set('postizMode', v ? 'draft' : 'now')}
                        onLabel="Entwurf"
                        offLabel="Live"
                        tone="neutral"
                        ariaLabel="Als Entwurf an Postiz senden"
                    />
                </OptionRow>

                {liveAutopublish && (
                    <p className={css.caution} role="note">
                        ⚠ Automatisierung und automatische Freigabe sind aktiv: Beiträge gehen ohne
                        weitere Kontrolle sofort live.
                    </p>
                )}
            </Section>

            <Section
                title="Postiz-Verbindungen"
                description="Die Postiz-Integration je Plattform. Verbindungen werden in Postiz verwaltet."
            >
                {PLATFORMS.map(p => {
                    const id = (config.postizIntegrationIds || {})[p] || '';
                    const missing = config.enabled && !id;
                    return (
                        <div key={p} className={css.field}>
                            <div className={css.fieldHead}>
                                <label className={css.label} htmlFor={`integration-${p}`}>
                                    {PLATFORM_LABELS[p]} Integration-ID
                                </label>
                                {renderHealthPill(id)}
                            </div>
                            <input
                                id={`integration-${p}`}
                                className={css.input}
                                value={id}
                                onChange={e => setNested('postizIntegrationIds', p, e.target.value)}
                                placeholder="z. B. clw3x…"
                                spellCheck={false}
                            />
                            {missing && (
                                <p className={css.fieldWarn}>
                                    Ohne Integration-ID wird auf {PLATFORM_LABELS[p]} nicht
                                    veröffentlicht.
                                </p>
                            )}
                        </div>
                    );
                })}
                {healthError && (
                    <p className={css.hint}>Postiz-Status nicht verfügbar: {healthError}</p>
                )}
            </Section>

            <Section
                title="Instagram Stories (Storrito)"
                description="Story-Beiträge mit echtem, anklickbarem Link-Sticker — über Storrito statt Postiz (Instagrams offizielle API kann keinen Story-Link setzen)."
            >
                <OptionRow
                    title="Stories über Storrito posten"
                    helper={
                        config.storritoEnabled
                            ? 'Instagram-Story-Ausgänge gehen über Storrito mit nativem „ZUM PROFIL“-Link-Sticker (Ziel: der Bitly-Kurzlink des Inserats).'
                            : 'Aus: Instagram-Stories laufen über Postiz — ohne anklickbaren Link.'
                    }
                >
                    <Switch
                        checked={!!config.storritoEnabled}
                        onChange={v => set('storritoEnabled', v)}
                        onLabel="An"
                        offLabel="Aus"
                        tone="success"
                        ariaLabel="Stories über Storrito posten"
                    />
                </OptionRow>
                {config.storritoEnabled && (
                    <div className={css.field}>
                        <div className={css.fieldHead}>
                            <span className={css.label}>Storrito-Verbindung</span>
                            {storritoHealthRows.length > 0 ? (
                                <div className={css.healthPills}>
                                    {storritoHealthRows.map((h, i) => (
                                        <React.Fragment key={h.integrationId || i}>
                                            {healthPill(h)}
                                        </React.Fragment>
                                    ))}
                                </div>
                            ) : (
                                <span className={`${css.statusPill} ${css.statusNeutral}`}>
                                    Status unbekannt
                                </span>
                            )}
                        </div>
                        {storritoPreflight && (
                            <p className={css.caution} role="note">
                                ⚠ Storrito ist aktiviert, aber nicht verbunden —
                                STORRITO_API_BASE_URL / STORRITO_API_TOKEN prüfen.
                            </p>
                        )}
                    </div>
                )}

                <div className={css.field}>
                    <label className={css.label} htmlFor="storritoInstagramUsername">
                        Instagram-Benutzername (in Storrito verbunden)
                    </label>
                    {accountUsernames.length > 0 && (
                        <select
                            id="storritoInstagramUsername"
                            className={css.input}
                            value={usernameSelectValue}
                            onChange={handleUsernameSelect}
                        >
                            <option value="">Konto wählen…</option>
                            {accountUsernames.map(u => (
                                <option key={u} value={u}>
                                    @{u}
                                </option>
                            ))}
                            <option value={CUSTOM_USERNAME}>Anderer Benutzername…</option>
                        </select>
                    )}
                    {showUsernameField && (
                        <input
                            id={
                                accountUsernames.length > 0
                                    ? 'storritoInstagramUsernameCustom'
                                    : 'storritoInstagramUsername'
                            }
                            className={`${css.input} ${
                                accountUsernames.length > 0 ? css.fallbackField : ''
                            }`}
                            value={config.storritoInstagramUsername || ''}
                            onChange={e => set('storritoInstagramUsername', e.target.value)}
                            placeholder="z. B. horsedeal"
                            spellCheck={false}
                            aria-label={
                                accountUsernames.length > 0
                                    ? 'Instagram-Benutzername manuell eingeben'
                                    : undefined
                            }
                        />
                    )}
                    {config.storritoEnabled && !config.storritoInstagramUsername && (
                        <p className={css.fieldWarn}>
                            Ohne verbundenen Instagram-Benutzernamen kann Storrito keine Story
                            posten.
                        </p>
                    )}
                </div>

                <div className={css.field}>
                    <label className={css.label} htmlFor="storritoLinkDesign">
                        Stil des Link-Stickers
                    </label>
                    <select
                        id="storritoLinkDesign"
                        className={`${css.input} ${css.inputNarrow}`}
                        value="rainbow"
                        disabled
                        aria-describedby="storritoLinkDesignHint"
                    >
                        <option value="rainbow">Regenbogen (4. Stil)</option>
                    </select>
                    <p className={css.hint} id="storritoLinkDesignHint">
                        Fest eingestellt für den nativen „ZUM PROFIL“-Link-Sticker in der Story.
                    </p>
                </div>
            </Section>

            <Section
                title="Erweitert"
                description="Feineinstellungen für den Veröffentlichungslauf."
            >
                <div className={css.field}>
                    <label className={css.label} htmlFor="postizMaxPerTick">
                        Max. Beiträge pro Durchlauf
                    </label>
                    <input
                        id="postizMaxPerTick"
                        className={`${css.input} ${css.inputNarrow} ${
                            showError('postizMaxPerTick') ? css.inputInvalid : ''
                        }`}
                        type="number"
                        min="1"
                        step="1"
                        value={config.postizMaxPerTick == null ? '' : config.postizMaxPerTick}
                        onChange={e => {
                            set(
                                'postizMaxPerTick',
                                e.target.value === '' ? '' : Number(e.target.value)
                            );
                            touch('postizMaxPerTick');
                        }}
                        aria-invalid={showError('postizMaxPerTick') ? 'true' : undefined}
                    />
                    {showError('postizMaxPerTick') ? (
                        <p className={css.fieldError}>{errors.postizMaxPerTick}</p>
                    ) : (
                        <p className={css.hint}>
                            Wie viele Beiträge ein einzelner Lauf höchstens verarbeitet.
                        </p>
                    )}
                </div>
            </Section>

            <Section
                title="Brand-Grafiken"
                description="Eigene Logos, Icons und Grafiken für den Design-Editor. Sie erscheinen dort im „Grafik“-Auswahlfeld."
            >
                <BrandAssetsPanel />
            </Section>

            <Section
                title="Browser-Erweiterung (Community-Posts)"
                description="Schlüssel für die HorseDeal Community-Poster Erweiterung, mit der Facebook-Community-Beiträge manuell gepostet werden."
            >
                <div className={css.field}>
                    <label className={css.label} htmlFor="extensionApiKey">
                        Erweiterungs-API-Schlüssel
                    </label>
                    <div className={css.fieldHead}>
                        <input
                            id="extensionApiKey"
                            className={css.input}
                            value={maskedKey}
                            readOnly
                            spellCheck={false}
                            onFocus={e => e.target.select()}
                        />
                    </div>
                    <div className={css.actionBarButtons}>
                        <Button
                            className={css.secondaryButton}
                            type="button"
                            onClick={() => setExtKeyShown(s => !s)}
                            disabled={!extKey}
                        >
                            {extKeyShown ? 'Verbergen' : 'Anzeigen'}
                        </Button>
                        <Button
                            className={css.secondaryButton}
                            type="button"
                            onClick={handleCopyKey}
                            disabled={!extKey}
                        >
                            {extKeyCopied ? '✓ Kopiert' : 'Kopieren'}
                        </Button>
                        <InlineConfirm
                            triggerLabel="Neu generieren"
                            triggerClassName={css.secondaryButton}
                            question="Neuen Schlüssel generieren?"
                            note="Bestehende Erweiterungen müssen den neuen Schlüssel eintragen."
                            confirmLabel="Generieren"
                            busy={extKeyBusy}
                            onConfirm={handleRotateKey}
                        />
                    </div>
                    {extKeyError ? (
                        <p className={css.fieldError} role="alert">
                            {extKeyError}
                        </p>
                    ) : null}
                    <p className={css.hint}>
                        In der Erweiterung einfügen, um die Warteschlange freigegebener
                        Community-Beiträge zu laden. <strong>Neu generieren</strong> macht den alten
                        Schlüssel sofort ungültig.
                    </p>
                </div>
            </Section>

            <div className={css.actionBar}>
                <span className={`${css.dirtyHint} ${dirty ? css.dirtyHintActive : ''}`}>
                    {dirty ? '● Ungespeicherte Änderungen' : 'Alle Änderungen gespeichert'}
                </span>
                <div className={css.actionBarButtons}>
                    <Button
                        className={css.secondaryButton}
                        type="button"
                        disabled={!dirty || saveSettingsInProgress}
                        onClick={handleDiscard}
                    >
                        Verwerfen
                    </Button>
                    <Button
                        className={css.actionButton}
                        type="button"
                        inProgress={saveSettingsInProgress}
                        ready={settingsSaved && !dirty}
                        disabled={!canSave}
                        onClick={handleSave}
                    >
                        Speichern
                    </Button>
                </div>
            </div>
        </div>
    );
};

const mapStateToProps = state => {
    const {
        settings,
        fetchSettingsInProgress,
        fetchSettingsError,
        saveSettingsInProgress,
        saveSettingsError,
        settingsSaved,
        health,
        healthError,
        runtimeMode,
    } = state.AdminSocialPostsPage;
    return {
        settings,
        fetchSettingsInProgress,
        fetchSettingsError,
        saveSettingsInProgress,
        saveSettingsError,
        settingsSaved,
        health,
        healthError,
        runtimeMode,
    };
};

const mapDispatchToProps = dispatch => ({
    onFetchSettings: () => dispatch(fetchSettings()),
    onFetchHealth: () => dispatch(fetchHealth()),
    onSave: config => dispatch(saveSettings(config)),
    onFetchExtensionKey: () => dispatch(fetchExtensionKey()),
    onRotateExtensionKey: () => dispatch(rotateExtensionKey()),
    onFetchStorritoAccounts: () => dispatch(fetchStorritoAccounts()),
});

export default connect(mapStateToProps, mapDispatchToProps)(SocialSettingsForm);
